import React from 'react';
import Panel from '../../components/ui/Panel';
import ComponentShowcase from '../../components/ui/ComponentShowcase';
import Checkbox from '../../components/ui/Checkbox';

const basicCode = `<Checkbox>I agree to the terms</Checkbox>`;
const statesCode = `<Checkbox>Unchecked</Checkbox>
<Checkbox defaultChecked>Checked</Checkbox>
<Checkbox disabled>Disabled</Checkbox>
<Checkbox defaultChecked disabled>Disabled checked</Checkbox>`;

const CheckboxPage: React.FC = () => {
  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Checkbox</h1>
      <p className="text-osrs-yellow mb-8">
        Settings rows use a small inset box. A yellow tick marks the option as on. The tick is drawn by the component, not by a separate icon font.
      </p>

      <section id="examples" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Examples</h2>
        <ComponentShowcase
          id="basic"
          title="Basic Checkbox"
          description="Click the row. The box and the label are one control."
          component={<Checkbox>I agree to the terms</Checkbox>}
          code={basicCode}
        />
      </section>

      <section id="states" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">States</h2>
        <ComponentShowcase
          title="Unchecked, checked, and disabled"
          description="Disabled options stay visible and cannot be toggled."
          component={
            <div className="flex flex-col gap-3 items-start">
              <Checkbox>Unchecked</Checkbox>
              <Checkbox defaultChecked>Checked</Checkbox>
              <Checkbox disabled>Disabled</Checkbox>
              <Checkbox defaultChecked disabled>Disabled checked</Checkbox>
            </div>
          }
          code={statesCode}
        />
      </section>

      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        <Panel>
          <h3 id="when-to-use" className="text-xl text-osrs-gold font-bold mb-3">When to Use</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use for settings that are on or off.</li>
            <li>Use one box per option when several can be on together.</li>
            <li>Put the option name in the checkbox children so the accessible name matches the row.</li>
          </ul>
        </Panel>
      </section>

      <section id="api-reference" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">API Reference</h2>
        <Panel>
          <p className="text-osrs-yellow">
            Checkbox accepts the attributes of a native checkbox except type. Children render as the label. checked, defaultChecked, disabled, and onChange behave like the native control.
          </p>
        </Panel>
      </section>
    </div>
  );
};

export default CheckboxPage;
